import { Grid, Button, Paper, Box } from '@mui/material';
import APIReport from '@apps/nnc/services/nnc';
import { useTranslation } from 'react-i18next';

import { JSX, useRef, useState, useEffect } from 'react';
import Skeleton from '@mui/material/Skeleton';
import fileDownload from 'js-file-download';
import { DatePickerElement } from 'react-hook-form-mui/date-pickers';
import {
  AutocompleteElement,
  FormContainer,
  useForm,
} from 'react-hook-form-mui';
import { PageTitle } from '@/components/atoms';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';
import UnitAPI from '@apps/localization/services/unit';
import { formatDateSafely } from '@/utils/dateUtils';
import dayjs from 'dayjs';
import CountryAPI from '@apps/localization/services/country';

interface FormData {
  start_date: dayjs.Dayjs | null;
  end_date: dayjs.Dayjs | null;
  country: Array<{ id: number; label: string }> | null;
  unit: Array<{ id: number; label: string }> | null;
  status: Array<{ id: number; label: string }> | null;
}

export default function NNCReportPage(): JSX.Element {
  const tipo = useRef<number>(1);
  const { t } = useTranslation();
  const formContext = useForm<FormData>({
    defaultValues: {
      start_date: dayjs().startOf('month'),
      end_date: dayjs().endOf('month'),
      country: null,
      unit: null,
      status: null,
    },
  });
  const { getValues } = formContext;
  const [loading, setLoading] = useState(false);
  const [allUnits, setAllUnits] = useState<
    Array<{ id: number; label: string }>
  >([]);
  const [allCountries, setAllCountries] = useState<
    Array<{ id: number; label: string }>
  >([]);

  // Função para buscar todas as unidades
  const fetchAllUnits = async () => {
    try {
      const response = await UnitAPI.select({});
      setAllUnits(response.data || []);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  // Função para buscar todos os países
  const fetchAllCountries = async () => {
    try {
      const response = await CountryAPI.select({});
      setAllCountries(response.data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  // Função para selecionar toda a gestão
  const selectAllManagement = () => {
    formContext.setValue('country', allCountries);
    formContext.setValue('unit', allUnits);
    formContext.setValue('status', opt);
  };

  // Buscar unidades quando o componente carrega
  useEffect(() => {
    fetchAllUnits();
    fetchAllCountries();
  }, []);

  const opt = [
    {
      id: 1,
      label: t('Pending'),
    },
    {
      id: 2,
      label: t('On Going'),
    },
    {
      id: 3,
      label: t('Completed'),
    },
    {
      id: 4,
      label: t('Unfounded'),
    },
  ];

  const onSubmit = async () => {
    setLoading(true);
    try {
      const params = getValues();

      const formatedParams = {
        ...params,
        start_date: formatDateSafely(params.start_date),
        end_date: formatDateSafely(params.end_date),
        status: params.status
          ? params.status.map((s: { id: number }) => s.id)
          : null,
        unit:
          params.unit && params.unit.length > 0
            ? params.unit.map((u: { id: number }) => u.id)
            : null,
        country:
          params.country && params.country.length > 0
            ? params.country.map((c: { id: number }) => c.id)
            : null,
      };

      const response = await APIReport.export(formatedParams);
      fileDownload(response.data, 'ActionPlans.xlsx');
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportX = async () => {
    tipo.current = 2;
    formContext.handleSubmit(onSubmit)();
  };

  return (
    <>
      <PageTitle title={`${t('Reports')} - ${t('Action Plans')}`} />
      <FormContainer formContext={formContext} onSuccess={onSubmit}>
        <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
          <Grid
            container
            sx={{
              p: 2,
              display: 'flex',
              width: '100%',
            }}
            alignItems="center"
            gap={3}
          >
            <Grid size={{ xs: 12, md: 1.5 }}>
              <DatePickerElement
                name="start_date"
                label={t('Start Date')}
                inputProps={{ sx: { width: '100%' } }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: false,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 1.5 }}>
              <DatePickerElement
                name="end_date"
                label={t('End Date')}
                inputProps={{ sx: { width: '100%' } }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: false,
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2.6 }}>
              <AsyncAutocompleteElement
                api={UnitAPI.select}
                name="unit"
                label={t('Unit')}
                multiple
                autocompleteProps={{
                  limitTags: 2,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2.6 }}>
              <AsyncAutocompleteElement
                api={CountryAPI.select}
                name="country"
                label={t('Country')}
                multiple
                autocompleteProps={{
                  limitTags: 2,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2.7 }}>
              <AutocompleteElement
                name="status"
                label={t('Status')}
                options={opt}
                multiple
                autocompleteProps={{
                  limitTags: 2,
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 12 }}>
              <Box display={'flex'} justifyContent="flex-end" gap={2}>
                <Button
                  sx={{ mr: 2 }}
                  type="button"
                  variant="outlined"
                  disableElevation
                  onClick={selectAllManagement}
                  color="primary"
                >
                  {t('All Management')}
                </Button>
                <Button
                  sx={{ mr: 2 }}
                  type="button"
                  variant="contained"
                  disableElevation
                  onClick={exportX}
                  color="secondary"
                >
                  {t('Export')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </FormContainer>
      {loading && (
        <Skeleton variant="rectangular" width={'100%'} height={600} />
      )}
    </>
  );
}
